import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import axiosInstance from "../../../api/Axiosinstance"
import { ENDPOINTS } from "../../../api/Endpoints"
import { authStore } from "../../../stores/auth.store"
import { useToast } from "../../../hooks/useToast"

const CODE_LENGTH = 6

export default function VerifyEmail() {
  const location = useLocation()
  const navigate = useNavigate()
  const inputsRef = useRef([])
  const toast = useToast()

  const extractAccessToken = (payload) =>
    payload?.accessToken ||
    payload?.token ||
    payload?.data?.accessToken ||
    payload?.data?.token ||
    payload?.access_token ||
    payload?.data?.access_token ||
    null

  const unwrapResponse = (response) => {
    if (!response) return {}
    return (
      response.data?.data?.profile ||
      response.data?.data?.user ||
      response.data?.data ||
      response.data?.profile ||
      response.data ||
      response.profile ||
      response
    )
  }

  const isAdminRole = (role) => {
    if (!role) return false
    const normalized = String(role).trim().toLowerCase()
    if (normalized.includes("admin")) return true
    return ["super_admin", "superadmin"].includes(normalized)
  }

  const email = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const raw = params.get("email") || "your@email.com"
    return raw.trim().toLowerCase()
  }, [location.search])

  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""))
  const [resendCooldown, setResendCooldown] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    inputsRef.current[0]?.focus()
  }, [])

  useEffect(() => {
    let timer
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((t) => t - 1), 1000)
    }
    return () => timer && clearTimeout(timer)
  }, [resendCooldown])

  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return
    const next = [...code]
    next[index] = value
    setCode(next)
    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      const prevIndex = index - 1
      const next = [...code]
      next[prevIndex] = ""
      setCode(next)
      inputsRef.current[prevIndex]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH)
    if (!pasted) return
    const next = pasted.split("")
    while (next.length < CODE_LENGTH) next.push("")
    setCode(next)
    const lastFilled = Math.min(pasted.length, CODE_LENGTH) - 1
    inputsRef.current[lastFilled]?.focus()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const otp = code.join("").trim()
    if (otp.length < CODE_LENGTH) {
      toast.error("Please enter the full verification code")
      return
    }
    setSubmitting(true)

    try {
      // Verify email code
      const response = await axiosInstance.post("/auth/verify-email", {
        email,
        otp,
      })

      const payload = response?.data || {}
      const data = payload.data || payload
      const accessToken = extractAccessToken(payload) || extractAccessToken(data)
      let user = data?.user || data?.profile || data?.data?.user || data?.data || data

      if (accessToken) {
        // Fetch user object first so we know the role before saving the token
        if (!user || typeof user !== "object" || !user.email) {
          try {
            const accountResponse = await axiosInstance.get(ENDPOINTS.AUTH.ACCOUNT)
            user = unwrapResponse(accountResponse)
          } catch (fetchError) {
            user = user || { email }
          }
        }

        const role = user?.role
        authStore.setToken(accessToken, false, role)  // no rememberMe on email verify

        const redirectTo = isAdminRole(role)
          ? "/admin"
          : role === "serviceProvider" || role === "assistant"
            ? "/assistant/kyc"
            : "/user/travel-assistant"

        authStore.setUser(user || { email })
        navigate(redirectTo, { replace: true })
        return
      }

      toast.success("Email verified. Please log in to continue.")
      setTimeout(() => navigate("/auth/login", { replace: true }), 800)
    } catch (err) {
      toast.apiError(err, "Incorrect verification code.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return
    setCode(Array(CODE_LENGTH).fill(""))
    inputsRef.current[0]?.focus()
    setResendCooldown(30)
    try {
      await axiosInstance.post("/auth/resend-verification-code", { email })
      toast.success("Verification code resent successfully!")
    } catch (err) {
      toast.apiError(err, "Unable to resend code.")
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4 text-gray-900">
      <div className="w-full max-w-md space-y-6 text-center">

        <div className="flex justify-center">
          <img src="/images/logo.png" alt="PULO Logo" className="w-20" />
        </div>

        <div className="space-y-2">
          <h1 className="text-3xl font-semibold">Verify Your Email</h1>
          <p className="text-sm text-gray-600 font-medium">
            We have sent a verification code to your email address
            <br />
            <span className="font-semibold">{email}</span>.{" "}
            <button
              type="button"
              onClick={() => navigate(-1)}
              className=" text-[#7a0051] underline underline-offset-4"
            >
              Wrong email?
            </button>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-center gap-3" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="h-14 w-14 rounded-lg border border-[#7a0051] text-center text-xl font-semibold text-gray-900 outline-none transition focus:ring-2 focus:ring-[#7a0051]/30"
              />
            ))}
          </div>
          <div className="text-sm text-gray-800">
            Didn&apos;t get any code?{" "}
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="font-semibold text-[#7a0051] underline underline-offset-4 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend"}
            </button>
          </div>

         <button
            type="submit"
            disabled={submitting}
            className={`w-full rounded-lg py-3 text-base font-semibold text-white shadow-md transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7a0051] ${
              submitting ? "bg-gray-400 cursor-not-allowed" : "bg-[#61013B] hover:bg-[#640140]"
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="h-5 w-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Submitting...
              </span>
            ) : (
              "Submit"
            )}
          </button>

        </form>
      </div>
    </div>
  )
}
